/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited 2023.
 */

// import { Attachment } from '@/components/blocks/Attachment';
// import { LocalizationWithComponent } from '@/components/blocks/LocalizationWithComponent';
import {
	productDescriptionTitleSX,
	productDescriptionTextSX,
	productDeyailsTitleSX,
} from '@/components/blocks/ProductDetails/styles/productDescriptionDetails';
// import { TabData, Tabs } from '@/components/blocks/Tabs';
import { TYPES } from '@/data/constants/product';
import { useProductDetails } from '@/data/Content/ProductDetails';
import { useSkuListTable } from '@/data/Content/SkuListTable';
import { ContentContext } from '@/data/context/content';
import { useLocalization } from '@/data/Localization';
import {
	// Attachment as AttachmentType,
	// ProductAttribute,
	ProductAttributeValue,
} from '@/data/types/Product';
import { getProductDisplayInfo } from '@/utils/getProductDisplayInfo';
import { productIsA } from '@/utils/productIsA';
import { Box, Typography, Stack, Grid } from '@mui/material';
// import HTMLReactParser from 'html-react-parser';
import {
	// uniq,
	uniqWith,
} from 'lodash';
import React, { FC, useContext } from 'react';
import { parseHTML } from '@/utils/parseHTML';

const comparator = (l: ProductAttributeValue, r: ProductAttributeValue) => l.value === r.value;
const emptyAttrValues: ProductAttributeValue[] = [];

interface ProductDescriptionDetailsProps {
	isDpElectronico: boolean;
}

const identifiersExcludedElectronics = ['Genero', 'attr-talla-dp'];
const identifiersExcludedCommon = ['attr-talla-dp'];

export const ProductDescriptionDetails: FC<ProductDescriptionDetailsProps> = ({
	isDpElectronico,
}) => {
	const localization = useLocalization('productDetail');
	const { selection, product } = useContext(ContentContext) as ReturnType<
		typeof useProductDetails
	> &
		ReturnType<typeof useSkuListTable>;
	const sku = !productIsA(product, TYPES.bundle) ? selection?.sku : product;
	const {
		tabsData: { longDescription, descriptiveAttributes },
	} = getProductDisplayInfo(sku, product);
	const codes = product?.keyword.split(',') ?? ['', ''];

	const cleanHTML = longDescription.replace(/<p[^>]*>(\s|&nbsp;|<br>)*<\/p>/g, '');
	const descriptiveAttributesFiltered = isDpElectronico
		? descriptiveAttributes?.filter(
				({ identifier }) => !identifiersExcludedElectronics.includes(identifier)
		  )
		: descriptiveAttributes?.filter(
				({ identifier }) => !identifiersExcludedCommon.includes(identifier)
		  );

	return (
		<Stack>
			<Stack sx={{ marginTop: '21px', mr: { xs: 0, md: '40px' } }}>
				<Box display={'flex'} gap={'5px'} alignItems={'center'}>
					<Typography
						component={'h6'}
						sx={{ fontSize: 'var(--font-size-caption)', color: '#929292' }}
					>
						{'Modelo: '} {codes[0]}
					</Typography>
					<Typography
						component={'span'}
						sx={{ fontSize: 'var(--font-size-caption)', color: '#929292' }}
					>
						{'|'}
					</Typography>

					<Typography
						component={'h6'}
						sx={{ fontSize: 'var(--font-size-caption)', color: '#929292' }}
					>
						{'Código: '} {codes[1]}
					</Typography>
				</Box>
				<Typography component={'h2'} sx={productDescriptionTitleSX}>
					{localization.Description.t()}
				</Typography>
				<Typography sx={productDescriptionTextSX}>{parseHTML(cleanHTML)}</Typography>
			</Stack>
			<Stack sx={{ marginTop: '20px' }}>
				<Typography component={'h2'} sx={productDeyailsTitleSX}>
					{localization.ProductDetails.t()}
				</Typography>
				{/* <DescriptiveAttributes/> */}
				<React.Fragment>
					<Stack
						justifyContent="center"
						alignItems="flex-start"
						direction={'column'}
						spacing={''}
						sx={{
							marginTop: '10px',
						}}
					></Stack>
					{descriptiveAttributes?.length ? (
						<Box
							data-testid="product-details-container"
							id="product-details-container"
							sx={{
								display: 'flex',
								flexWrap: 'wrap',
							}}
						>
							<Grid container columnSpacing={2} rowSpacing={1}>
								{descriptiveAttributesFiltered.map(
									({ identifier, name, values = emptyAttrValues }) => (
										<Grid
											item
											key={`product-attribute-${identifier}`}
											data-testid={`product-attribute-${identifier}`}
											id={`product-attribute-${identifier}`}
										>
											<Typography
												component={'li'}
												sx={{
													fontSize: {
														xs: 'var(--font-size-default)',
														sm: 'var(--font-size-body)',
													},
													color: '#2C2C2C',
													fontWeight: 'normal',
													letterSpacing: '0px',
													display: 'inline',
													marginRight: '5px',
												}}
											>
												{`• ${name}`}
											</Typography>
											{uniqWith(values, comparator).map(({ identifier: vIdentifier, value }, _) => (
												<Typography
													component={'li'}
													sx={{
														fontSize: {
															xs: 'var(--font-size-default)',
															sm: 'var(--font-size-body)',
														},
														color: '#2C2C2C',
														fontWeight: 'normal',
														letterSpacing: '0px',
														display: 'inline',
													}}
													key={vIdentifier}
												>
													{value}
												</Typography>
											))}
										</Grid>
									)
								)}
							</Grid>
						</Box>
					) : null}
				</React.Fragment>
			</Stack>
		</Stack>
	);
};
